import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, lastValueFrom, Subject, Subscription } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { POrderPortailCart, POrderPortailCartItem } from "../m-op-app.models";
import { HandleError } from "src/app/utils/network-error-handler";

@Injectable({
    providedIn: 'root'
})
export class POrderPortailAppService {
    appInitiated = false;
    appSettings: any = null;
    appLoading$ = new BehaviorSubject<boolean>(false);
    cart: POrderPortailCart;
    cartUpdated$ = new Subject<{ action: 'add' | 'update' | 'remove' | 'init', item: POrderPortailCartItem | null }>();
    cartProcessing$ = new BehaviorSubject(false);

    constructor(
        private _httpClient: HttpClient,
        private _apiService: ApiService
    ) {
    }

    async doInitApp() {
        try {
            await this.doLoadAppSettingsAction();
            await this.doLoadAccountCartAction();
            this.appInitiated = true;
        } catch (error) {
            console.error('Error initializing app:', error);
        }
    }

    async doLoadAppSettingsAction() {
        this.appSettings = await lastValueFrom(this._httpClient.get<any>(this._apiService.endPointsFO.doGetSettingsAction_FOPOrderPortailSettings));
    }

    doCalculateProductAction(dto: any) {
        return this._httpClient.post<any>(this._apiService.endPointsFO.doGetCalculateProductAction_FOPOrderPortailProducts, dto).pipe(catchError(error => HandleError(error)));
    }

    async doAddProductToCartAction(dto: any) {
        return new Promise<void>(async (resolve, reject) => {
            try {
                this.cartProcessing$.next(true);
                await lastValueFrom(this._httpClient.post<void>(this._apiService.endPointsFO.doAddProductToCartAction_FOPOrderPortailCart, { LIGNES: [dto]}).pipe(catchError(error => HandleError(error))));
                this.cart = await this.doGetCartAction();
                this.cartUpdated$.next({ action: 'add', item: null });
                this.cartProcessing$.next(false);
                resolve();
            } catch(error: any) {
                this.cartProcessing$.next(false);
                reject(error);
            }
        });
    }

    async doUpdateItemInCart(cartItem: POrderPortailCartItem) {
        return new Promise<void>(async (resolve, reject) => {
            try {
                this.cartProcessing$.next(true);
                await lastValueFrom(this._httpClient.post<void>(this._apiService.endPointsFO.doUpdateProductQuantityInCartAction_FOPOrderPortailCart, cartItem).pipe(catchError(error => HandleError(error))));
                this.cart = await this.doGetCartAction();
                this.cartUpdated$.next({ action: 'update', item: cartItem });
                this.cartProcessing$.next(false);
                resolve();
            } catch(error: any) {
                this.cartProcessing$.next(false);
                reject(error);
            }
        });
    }

    async doRemoveItemInCart(cartItem: POrderPortailCartItem) {
        return new Promise<void>(async (resolve, reject) => {
            try {
                this.cartProcessing$.next(true);
                await lastValueFrom(this._httpClient.post<void>(this._apiService.endPointsFO.doRemoveProductInCartAction_FOPOrderPortailCart, { DL_ID: cartItem.DL_ID }).pipe(catchError(error => HandleError(error))));
                this.cart = await this.doGetCartAction();
                this.cartUpdated$.next({ action: 'remove', item: cartItem });
                this.cartProcessing$.next(false);
                resolve();
            } catch(error: any) {
                this.cartProcessing$.next(false);
                reject(error);
            }
        });
    }

    doClearCart() {
        return new Promise<void>(async (resolve, reject) => {
            try {
                this.cartProcessing$.next(true);
                await lastValueFrom(this._httpClient.post<void>(this._apiService.endPointsFO.doClearCartAction_FOPOrderPortailCart, {}).pipe(catchError(error => HandleError(error))));
                this.cart = await this.doGetCartAction();
                this.cartUpdated$.next({ action: 'init', item: null });
                this.cartProcessing$.next(false);
                resolve();
            } catch(error: any) {
                this.cartProcessing$.next(false);
                reject(error);
            }
        });
    }

    doSoftClearCart() {
        this.cart = new POrderPortailCart();
        this.cartUpdated$.next({ action: 'init', item: null });
    }

    async doGetCartAction() {
        return await lastValueFrom(this._httpClient.get<POrderPortailCart>(this._apiService.endPointsFO.doGetCartAction_FOPOrderPortailCart).pipe(catchError(error => HandleError(error))));
    }

    async doLoadAccountCartAction() {
        this.cart = await this.doGetCartAction();
        this.cartUpdated$.next({ action: 'init', item: null });
    }
}

